/**
 * API 工具函数
 * 统一处理 API 调用，解决 HTTPS/HTTP 混合内容问题
 * 
 * 说明：
 * 1. 在钉钉 HTTPS 环境中，使用相对路径 `/api/...`，通过 Nginx 代理到后端服务
 * 2. 在开发环境中，使用相对路径 `/api/...`，通过 webpack dev server proxy 代理
 * 3. 相对路径会自动使用当前页面的协议和域名，避免 Mixed Content 错误
 */

/**
 * 获取 API 基础 URL
 * 根据环境自动判断使用相对路径或完整 URL
 */
export function getApiBaseUrl(): string {
  // 始终使用相对路径，依赖代理配置
  // - 开发环境：webpack dev server proxy (package.json 中的 proxy 配置)
  // - 生产环境：Nginx 代理 (location /api/ 配置)
  return '';
}

/**
 * 构建完整的 API URL
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  // 确保 path 以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * 统一的 fetch 封装
 * 处理 HTTPS/HTTP 混合内容问题
 */
export async function apiFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = buildApiUrl(path);
  
  // 合并默认选项
  const defaultOptions: RequestInit = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  try {
    // 在 HTTPS 环境中，确保使用相对路径（通过代理）
    // 如果当前页面是 HTTPS，但 URL 是 HTTP，浏览器会阻止请求
    let finalUrl = url;
    
    // 如果当前页面是 HTTPS，但 URL 是绝对 HTTP URL，转换为相对路径
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      // 提取路径部分，使用相对路径
      try {
        const urlObj = new URL(url);
        finalUrl = urlObj.pathname + urlObj.search;
        console.warn('[API] 检测到 HTTPS 环境中的 HTTP URL，已转换为相对路径:', {
          original: url,
          converted: finalUrl
        });
      } catch (e) {
        // URL 解析失败，使用原始 URL
        console.warn('[API] URL 解析失败，使用原始 URL:', url);
      }
    }
    
    const response = await fetch(finalUrl, mergedOptions);
    
    // 如果响应不是 OK，抛出错误
    if (!response.ok) {
      const errorText = await response.text().catch(() => '未知错误');
      
      // 尝试解析 JSON 错误信息
      let errorDetail = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorDetail = errorJson.error;
          // 如果是权限错误，提供申请链接
          if (errorDetail.includes('qyapi_cspace_base')) {
            errorDetail += '\n\n请访问钉钉开放平台申请权限：\n' +
              'https://open-dev.dingtalk.com/appscope/apply?content=dinglhh352e4cpwkhdyn%23qyapi_cspace_base';
          }
        }
      } catch (e) {
        // 不是 JSON，使用原始文本
      }
      
      throw new Error(
        `API 请求失败: ${response.status} ${response.statusText}\n` +
        `URL: ${finalUrl}\n` +
        `错误详情: ${errorDetail}`
      );
    }
    
    return response;
  } catch (error: any) {
    // 如果是混合内容错误，提供更友好的错误信息
    if (error.message?.includes('Mixed Content') || 
        error.message?.includes('HTTPS') ||
        error.message?.includes('insecure')) {
      console.error('[API] Mixed Content 错误:', {
        url,
        currentProtocol: window.location.protocol,
        currentHost: window.location.host
      });
      throw new Error(
        'API 请求失败：HTTPS/HTTP 混合内容问题。\n\n' +
        '解决方案：\n' +
        '1. 开发环境：确保 webpack dev server proxy 配置正确（package.json 中的 proxy）\n' +
        '2. 生产环境：确保 Nginx 配置了 location /api/ 代理到后端服务\n' +
        '3. 后端服务：确保后端服务运行在配置的端口（默认 3001）\n\n' +
        `请求 URL: ${url}\n` +
        `当前页面: ${window.location.protocol}//${window.location.host}`
      );
    }
    
    // 如果是网络错误，提供更详细的错误信息
    if (error.message?.includes('Failed to fetch') || 
        error.message?.includes('NetworkError') ||
        error.message?.includes('404')) {
      console.error('[API] 网络错误:', {
        url,
        error: error.message
      });
      throw new Error(
        `API 请求失败：无法连接到服务器。\n\n` +
        `可能的原因：\n` +
        `1. 后端服务未启动（检查端口 3001）\n` +
        `2. 代理配置不正确（检查 webpack proxy 或 Nginx 配置）\n` +
        `3. 网络连接问题\n\n` +
        `请求 URL: ${url}`
      );
    }
    
    throw error;
  }
}

